import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PlugZap, RefreshCw, CheckCircle2, XCircle, Database, Euro, AlertTriangle, Filter, PackageX } from "lucide-react";

export default function AdminMexal() {
    const [cfg, setCfg] = useState(null);
    const [password, setPassword] = useState("");
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState(null);
    const [runs, setRuns] = useState([]);
    const [saving, setSaving] = useState(false);
    const [pricesSyncing, setPricesSyncing] = useState(false);
    const [pricesResult, setPricesResult] = useState(null);
    const [recon, setRecon] = useState(null);
    const [reconLoading, setReconLoading] = useState(false);

    const load = () => {
        return Promise.all([
            api.get("/admin/config"),
            api.get("/admin/mexal/runs"),
            api.get("/admin/mexal/reconciliation").catch(() => ({ data: null })),
        ]).then(([{ data: c }, { data: r }, { data: rc }]) => {
            setCfg(c);
            setRuns(r.items || []);
            setRecon(rc);
            return r.items || [];
        });
    };

    const refreshRecon = async () => {
        setReconLoading(true);
        try {
            const { data } = await api.get("/admin/mexal/reconciliation");
            setRecon(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore riconciliazione");
        } finally {
            setReconLoading(false);
        }
    };
    // Prevent the anti-pattern of `useEffect(load, [])` (returning a Promise as cleanup fn).
    // On mount: refresh state; if a run is still 'running', resume polling automatically.
    useEffect(() => {
        load().then((items) => {
            const running = items.find((x) => x.status === "running");
            if (running) resumePoll(running.id);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const patchMexal = (patch) => setCfg((c) => ({ ...c, mexal: { ...c.mexal, ...patch } }));

    const save = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        try {
            const payload = { mexal: cfg.mexal };
            if (password) payload.mexal_password = password;
            // Numero listino Mexal → base_price_public (top-level in AppConfig).
            if (typeof cfg.mexal_price_list_number === "number") {
                payload.mexal_price_list_number = cfg.mexal_price_list_number;
            }
            await api.put("/admin/config", payload);
            toast.success("Configurazione Mexal salvata");
            setPassword("");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
        } finally {
            setSaving(false);
        }
    };

    const syncPrices = async () => {
        setPricesSyncing(true);
        setPricesResult(null);
        try {
            const { data } = await api.post("/admin/mexal/sync-prices");
            setPricesResult(data);
            toast.success(
                `Prezzi sincronizzati (listino ${data.listino_num}): ` +
                `${data.updated_formats} formati aggiornati · ${data.override_kept} override preservati`
            );
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore sync prezzi");
        } finally {
            setPricesSyncing(false);
        }
    };

    const testConn = async () => {
        setTesting(true); setTestResult(null);
        try {
            const { data } = await api.post("/admin/mexal/test");
            setTestResult(data);
            if (data.ok) toast.success("Connessione Mexal riuscita");
            else toast.error("Connessione fallita");
        } catch (err) {
            setTestResult({ ok: false, message: err.response?.data?.detail || err.message });
        } finally { setTesting(false); }
    };

    // Extracted polling helper so both syncNow and mount-time resume use it.
    const resumePoll = (runId) => {
        setSyncing(true);
        const started = Date.now();
        const maxMs = 15 * 60 * 1000;
        const poll = async () => {
            try {
                const { data: rr } = await api.get("/admin/mexal/runs");
                const items = rr.items || [];
                const found = items.find((x) => x.id === runId);
                if (found && (found.status === "completed" || found.status === "aborted" || found.status === "error")) {
                    setSyncResult(found);
                    setRuns(items);
                    setSyncing(false);
                    if (found.status === "completed") {
                        toast.success(`Sync completato: +${found.new_created || 0} nuovi · ${found.updated_stock || 0} giacenze aggiornate`);
                    } else {
                        toast.error(`Sync ${found.status}: ${found.reason || "vedi log"}`);
                    }
                    return;
                }
                if (Date.now() - started > maxMs) {
                    toast.error("Sync ancora in corso oltre il limite: consulta lo storico.");
                    setSyncing(false);
                    return;
                }
                setTimeout(poll, 3000);
            } catch (err) {
                toast.error("Errore durante il polling dello stato sync");
                setSyncing(false);
            }
        };
        setTimeout(poll, 2000);
    };

    const syncNow = async () => {
        setSyncResult(null);
        try {
            const { data } = await api.post("/admin/mexal/sync-now");
            toast.info("Sync avviato in background. Aggiornamento in corso…");
            resumePoll(data.run_id);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Impossibile avviare il sync");
        }
    };

    if (!cfg) return <div className="text-[hsl(var(--muted-foreground))]">Caricamento…</div>;

    return (
        <div className="max-w-3xl" data-testid="admin-mexal-page">
            <h1 className="text-3xl mb-2">Mexal (Passepartout Live)</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6 max-w-xl">
                Connessione WebAPI per sincronizzazione articoli e giacenze in tempo reale.
                Utente dedicato appartenente al gruppo "Servizi WebApi" richiesto lato Mexal.
            </p>

            <div className="flex gap-3 mb-8">
                <button onClick={testConn} disabled={testing} className="co-btn-ghost" data-testid="mexal-test-btn">
                    <PlugZap className="w-4 h-4" />
                    {testing ? "Verifica in corso…" : "Testa connessione"}
                </button>
                <button onClick={syncNow} disabled={syncing || cfg.freeze} className="co-btn-primary" data-testid="mexal-sync-now-btn">
                    <RefreshCw className="w-4 h-4" />
                    {syncing ? "Sync in corso…" : "Sincronizza ora"}
                </button>
            </div>

            {testResult && (
                <div className={`mb-6 p-4 rounded-sm border ${testResult.ok ? "border-green-600 bg-green-50" : "border-[hsl(var(--destructive))] bg-red-50"}`} data-testid="mexal-test-result">
                    <div className="flex items-start gap-3">
                        {testResult.ok
                            ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-green-700 shrink-0" strokeWidth={1.5} />
                            : <XCircle className="w-4 h-4 mt-0.5 text-[hsl(var(--destructive))] shrink-0" strokeWidth={1.5} />}
                        <div className="text-sm flex-1">
                            <div className="font-medium">{testResult.message}</div>
                            {testResult.detail && (
                                <pre className="mt-2 text-xs font-mono whitespace-pre-wrap text-[hsl(var(--muted-foreground))]">{JSON.stringify(testResult.detail, null, 2)}</pre>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {syncResult && (
                <div className="mb-6 p-4 rounded-sm border border-[hsl(var(--primary))]/40 bg-[#FDFBF7]" data-testid="mexal-sync-result">
                    <div className="co-caption mb-2">Ultimo sync ({syncResult.source})</div>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                        <Stat label="Scanned" value={syncResult.scanned} />
                        <Stat label="Nuovi" value={syncResult.new_created} highlight />
                        <Stat label="Stock aggiornato" value={syncResult.updated_stock} />
                        <Stat label="Merge proposti" value={syncResult.merge_proposed} />
                        <Stat label="Errori" value={syncResult.errors} />
                    </div>
                </div>
            )}

            <form onSubmit={save} className="space-y-6 bg-white border border-[hsl(var(--border))] rounded-sm p-5">
                <h2 className="text-xl">Credenziali</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Base URL WebAPI">
                        <Input value={cfg.mexal.base_url} onChange={(e) => patchMexal({ base_url: e.target.value })} data-testid="mexal-base-url" />
                    </Field>
                    <Field label="Dominio">
                        <Input value={cfg.mexal.dominio} onChange={(e) => patchMexal({ dominio: e.target.value })} data-testid="mexal-dominio" />
                    </Field>
                    <Field label="Utente">
                        <Input value={cfg.mexal.utente} onChange={(e) => patchMexal({ utente: e.target.value })} data-testid="mexal-utente" />
                    </Field>
                    <Field label={`Password ${cfg.mexal_password_set ? "(impostata)" : "(mancante)"}`}>
                        <Input type="password" placeholder="Nuova password (lascia vuoto per non cambiare)" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="mexal-password" />
                    </Field>
                </div>

                <h2 className="text-xl pt-2">Contesto aziendale</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Field label="Sigla Azienda">
                        <Input value={cfg.mexal.azienda} onChange={(e) => patchMexal({ azienda: e.target.value })} data-testid="mexal-azienda" />
                    </Field>
                    <Field label="Anno">
                        <Input value={cfg.mexal.anno} onChange={(e) => patchMexal({ anno: e.target.value })} data-testid="mexal-anno" />
                    </Field>
                    <Field label="Magazzino">
                        <Input value={cfg.mexal.magazzino} onChange={(e) => patchMexal({ magazzino: e.target.value })} data-testid="mexal-magazzino" />
                    </Field>
                </div>

                <h2 className="text-xl pt-2">Filtro articoli vino</h2>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">Default suggerito: um_principale = BT (bottiglia). La whitelist UM sotto ha priorità e permette import multi-formato.</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Field label="Campo">
                        <Input value={cfg.mexal.filter_field} onChange={(e) => patchMexal({ filter_field: e.target.value })} data-testid="mexal-filter-field" />
                    </Field>
                    <Field label="Condizione">
                        <Input value={cfg.mexal.filter_condizione} onChange={(e) => patchMexal({ filter_condizione: e.target.value })} data-testid="mexal-filter-condizione" />
                    </Field>
                    <Field label="Valore">
                        <Input value={cfg.mexal.filter_valore} onChange={(e) => patchMexal({ filter_valore: e.target.value })} data-testid="mexal-filter-valore" />
                    </Field>
                </div>

                <h2 className="text-xl pt-2">Whitelist unità di misura</h2>
                <p className="text-xs text-[hsl(var(--muted-foreground))] max-w-2xl">
                    Lista delle UM da importare separate da virgole (es. <code>BT, PZ, LT, CL</code>).
                    Solo <code>BT</code> = comportamento legacy (filtra server-side sul gestionale).
                    Multi-UM = scarica tutti gli articoli e filtra client-side (necessario per
                    importare articoli venduti a pezzo/litro/centilitro, es. distillati "coll").
                    Gli articoli non-BT passano dal classificatore anti-spazzatura: se non
                    risultano bevande vengono trattati secondo la policy sotto.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="UM ammesse (separate da virgola)">
                        <Input
                            value={(cfg.mexal.allowed_ums || ["BT"]).join(", ")}
                            onChange={(e) => patchMexal({
                                allowed_ums: e.target.value.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean),
                            })}
                            placeholder="BT, PZ, LT, CL"
                            data-testid="mexal-allowed-ums"
                        />
                    </Field>
                    <Field label="Policy non-bevande (UM diverse da BT)">
                        <select
                            value={cfg.mexal.non_beverage_action || "skip_and_report"}
                            onChange={(e) => patchMexal({ non_beverage_action: e.target.value })}
                            className="w-full border border-[hsl(var(--border))] rounded-sm px-3 py-2 text-sm bg-white"
                            data-testid="mexal-non-beverage-action"
                        >
                            <option value="skip_and_report">Non importare, elenca nel report (consigliato)</option>
                            <option value="import_as_altro">Importa come "altro" (nascosto al pubblico)</option>
                        </select>
                    </Field>
                </div>

                <h2 className="text-xl pt-2">Sincronizzazione</h2>
                <div className="flex items-center gap-3">
                    <Switch checked={cfg.mexal.sync_enabled} onCheckedChange={(v) => patchMexal({ sync_enabled: v })} data-testid="mexal-sync-enabled" />
                    <Label className="cursor-pointer text-sm">Attiva sync automatica programmata</Label>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Intervallo (minuti)">
                        <Input type="number" min="5" value={cfg.mexal.sync_interval_min} onChange={(e) => patchMexal({ sync_interval_min: Number(e.target.value) })} data-testid="mexal-sync-interval" />
                    </Field>
                    <Field label="Nuovi articoli max per run">
                        <Input type="number" min="0" value={cfg.mexal.max_new_per_run} onChange={(e) => patchMexal({ max_new_per_run: Number(e.target.value) })} data-testid="mexal-max-new" />
                    </Field>
                </div>

                <h2 className="text-xl pt-2">Listino prezzi Mexal</h2>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    Numero del listino Mexal da usare come <b>prezzo pubblico di sala</b>
                    (`base_price_public`). Confermalo col cliente per essere certo che sia
                    il listino "sala/carta" e non un listino ingrosso. Il <b>price_override</b>
                    impostato manualmente sul singolo formato vince sempre e non viene mai
                    sovrascritto dal sync.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Numero listino (1..99)">
                        <Input
                            type="number"
                            min="1"
                            max="99"
                            value={cfg.mexal_price_list_number ?? 1}
                            onChange={(e) => setCfg((c) => ({ ...c, mexal_price_list_number: Number(e.target.value) }))}
                            data-testid="mexal-price-list-number"
                        />
                    </Field>
                </div>

                <div>
                    <button type="submit" disabled={saving} className="co-btn-primary" data-testid="mexal-save-btn">
                        {saving ? "Salvataggio…" : "Salva configurazione Mexal"}
                    </button>
                </div>
            </form>

            <div className="mt-8 bg-white border border-[hsl(var(--border))] rounded-sm p-5">
                <h2 className="text-xl mb-2 flex items-center gap-2">
                    <Euro className="w-4 h-4" strokeWidth={1.5} />
                    Sync prezzi da Mexal (massivo)
                </h2>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4 max-w-2xl">
                    Rilegge il campo <code>prz_listino</code> per ogni SKU vino con listino
                    n° <b>{cfg.mexal_price_list_number ?? 1}</b> e aggiorna
                    <code> base_price_public</code>. I formati con <b>price_override</b>
                    impostato vengono <b>preservati</b>. Operazione idempotente.
                </p>
                <button
                    type="button"
                    onClick={syncPrices}
                    disabled={pricesSyncing || cfg.freeze}
                    className="co-btn-ghost"
                    data-testid="mexal-sync-prices-btn"
                >
                    <RefreshCw className="w-4 h-4" />
                    {pricesSyncing ? "Sincronizzazione prezzi in corso…" : "Sincronizza prezzi ora"}
                </button>

                {pricesResult && (
                    <div className="mt-4 p-4 rounded-sm border border-[hsl(var(--primary))]/40 bg-[#FDFBF7]" data-testid="mexal-sync-prices-result">
                        <div className="co-caption mb-2">Esito (listino {pricesResult.listino_num})</div>
                        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 text-sm">
                            <Stat label="Vini scanned" value={pricesResult.scanned} />
                            <Stat label="Formati aggiornati" value={pricesResult.updated_formats} highlight />
                            <Stat label="Vini toccati" value={pricesResult.wines_touched} />
                            <Stat label="Già allineati" value={pricesResult.already_current} />
                            <Stat label="Override tenuti" value={pricesResult.override_kept} />
                            <Stat label="Mancano in Mexal" value={pricesResult.missing_in_mexal} />
                        </div>
                    </div>
                )}
            </div>

            <div className="mt-8 bg-white border border-[hsl(var(--border))] rounded-sm p-5" data-testid="mexal-reconciliation-panel">
                <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                        <h2 className="text-xl mb-1 flex items-center gap-2">
                            <Filter className="w-4 h-4" strokeWidth={1.5} />
                            Riconciliazione ultimo sync
                        </h2>
                        <p className="text-sm text-[hsl(var(--muted-foreground))] max-w-2xl">
                            Report bidirezionale sull'ultimo sync completato: articoli in
                            magazzino Mexal NON entrati nel catalogo (esclusi per UM,
                            non-bevande, SKU non recuperabili, giacenza zero).
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={refreshRecon}
                        disabled={reconLoading}
                        className="co-btn-ghost text-xs"
                        data-testid="mexal-reconciliation-refresh-btn"
                    >
                        <RefreshCw className="w-3 h-3" />
                        {reconLoading ? "Aggiornamento…" : "Aggiorna"}
                    </button>
                </div>

                {!recon || !recon.run_meta ? (
                    <div className="italic text-sm text-[hsl(var(--muted-foreground))]">
                        {recon?.message || "Nessun run disponibile. Lancia una sincronizzazione."}
                    </div>
                ) : (
                    <>
                        <div className="text-xs text-[hsl(var(--muted-foreground))] mb-3">
                            Run <code>{recon.run_meta.id?.slice(0, 8)}…</code> · {recon.run_meta.source} ·
                            {" "}scanned {recon.run_meta.scanned} · nuovi creati <b className="text-[hsl(var(--primary))]">{recon.run_meta.new_created}</b> ·
                            {" "}completato {recon.run_meta.finished_at ? new Date(recon.run_meta.finished_at).toLocaleString("it-IT") : "—"}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-sm">
                            <Stat label={`Esclusi per UM (whitelist: ${(recon.allowed_ums || []).join(", ") || "—"})`} value={recon.totals?.filtered_by_um} />
                            <Stat label="SKU non recuperabili (/)" value={recon.totals?.unfetchable} />
                            <Stat label="Non-bevande scartate" value={recon.totals?.non_beverage} />
                            <Stat label="Stock zero (saltati)" value={recon.totals?.zero_stock} />
                        </div>

                        {recon.filtered_out_by_um && Object.keys(recon.filtered_out_by_um).length > 0 && (
                            <div className="mb-4">
                                <div className="co-caption text-[10px] mb-1">Distribuzione UM escluse</div>
                                <div className="flex flex-wrap gap-2 text-xs">
                                    {Object.entries(recon.filtered_out_by_um).map(([um, n]) => (
                                        <span key={um} className="px-2 py-0.5 rounded-sm border border-[hsl(var(--border))] bg-[hsl(var(--muted))]" data-testid={`recon-um-${um}`}>
                                            <b>{um}</b>: {n}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <ReconList
                            title="SKU non recuperabili — articoli con '/' nel codice"
                            hint="Passepartout WebAPI rifiuta path con slash. Rinomina in Mexal sostituendo '/' con '-' o '_'."
                            testid="recon-unfetchable"
                            icon={<AlertTriangle className="w-3 h-3 text-amber-700" strokeWidth={1.5} />}
                            items={[
                                ...(recon.unfetchable_skus?.linked || []).map((x) => ({ ...x, linkage: "linked" })),
                                ...(recon.unfetchable_skus?.new || []).map((x) => ({ ...x, linkage: "new" })),
                            ]}
                            renderRow={(x) => (
                                <>
                                    <code className="text-xs mr-2">{x.codice}</code>
                                    <span className="text-xs text-[hsl(var(--muted-foreground))] mr-2">
                                        [{x.linkage === "linked" ? "già in catalogo" : "mai importato"}]
                                    </span>
                                    <span className="text-xs">{x.descrizione}</span>
                                </>
                            )}
                        />

                        <ReconList
                            title="Non-bevande scartate (UM non-BT che il classificatore ha giudicato non-bevanda)"
                            hint="Da valutare manualmente. Se sono bevande, correggi la classificazione o cambia policy."
                            testid="recon-non-beverage"
                            icon={<PackageX className="w-3 h-3 text-[hsl(var(--muted-foreground))]" strokeWidth={1.5} />}
                            items={recon.skipped_non_beverage || []}
                            renderRow={(x) => (
                                <>
                                    <code className="text-xs mr-2">{x.codice}</code>
                                    <span className="co-badge-neutral text-[10px] mr-2">{x.um_principale}</span>
                                    <span className="text-xs">{x.descrizione}</span>
                                </>
                            )}
                        />
                    </>
                )}
            </div>

            <div className="mt-10">
                <h2 className="text-2xl mb-3">Storico sync</h2>
                {runs.length === 0 ? (
                    <div className="italic text-[hsl(var(--muted-foreground))]">Nessuna esecuzione registrata.</div>
                ) : (
                    <div className="space-y-2">
                        {runs.map((r, i) => (
                            <div key={i} className="bg-white border border-[hsl(var(--border))] rounded-sm p-3 flex items-center gap-4 text-sm" data-testid={`mexal-run-${i}`}>
                                <Database className="w-4 h-4 text-[hsl(var(--muted-foreground))]" strokeWidth={1.5} />
                                <div className="flex-1">
                                    <span className="co-badge-neutral mr-2">{r.source}</span>
                                    {r.status && r.status !== "completed" && (
                                        <span className={`text-xs mr-2 ${r.status === "running" ? "text-blue-700" : "text-[hsl(var(--destructive))]"}`}>[{r.status}]</span>
                                    )}
                                    <span className="text-xs text-[hsl(var(--muted-foreground))]">
                                        {r.finished_at
                                            ? new Date(r.finished_at).toLocaleString("it-IT")
                                            : r.started_at ? `avviato ${new Date(r.started_at).toLocaleString("it-IT")}` : "—"}
                                    </span>
                                </div>
                                <span className="text-xs">scanned <b>{r.scanned ?? "—"}</b> · new <b className="text-[hsl(var(--primary))]">{r.new_created ?? "—"}</b> · stock {r.updated_stock ?? "—"} · merge {r.merge_proposed ?? "—"} · err {r.errors ?? "—"}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="co-caption mb-1.5 block">{label}</Label>
            {children}
        </div>
    );
}

function Stat({ label, value, highlight }) {
    return (
        <div>
            <div className="co-caption text-[10px]">{label}</div>
            <div className={highlight ? "text-[hsl(var(--primary))] text-lg" : "text-base"}>{value ?? 0}</div>
        </div>
    );
}

function ReconList({ title, hint, items, renderRow, icon, testid }) {
    const [expanded, setExpanded] = useState(false);
    if (!items || items.length === 0) return null;
    const preview = expanded ? items : items.slice(0, 5);
    return (
        <div className="mt-4" data-testid={testid}>
            <div className="flex items-center gap-2 mb-1">
                {icon}
                <div className="text-sm font-medium">{title}</div>
                <span className="co-badge-neutral text-[10px]">{items.length}</span>
            </div>
            {hint && <div className="text-xs text-[hsl(var(--muted-foreground))] mb-2">{hint}</div>}
            <ul className="space-y-1">
                {preview.map((x, i) => (
                    <li key={i} className="text-sm py-1 px-2 rounded-sm bg-[hsl(var(--muted))]/40 border border-[hsl(var(--border))]">
                        {renderRow(x)}
                    </li>
                ))}
            </ul>
            {items.length > 5 && (
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className="text-xs text-[hsl(var(--primary))] mt-2 hover:underline"
                    data-testid={`${testid}-toggle`}
                >
                    {expanded ? `Nascondi (${items.length - 5})` : `Mostra tutti (+${items.length - 5})`}
                </button>
            )}
        </div>
    );
}
